import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';
import { Portal } from './Portal';
import { cn } from '@/utils/cn';

export interface SelectOption {
    value: string;
    label: string;
    icon?: React.ReactNode;
    description?: string;
    actionSlot?: React.ReactNode;
}

interface CustomSelectProps {
    value: string | string[];
    onChange: (value: any) => void;
    options: SelectOption[];
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    searchable?: boolean;
    isMulti?: boolean;
    showMultiActions?: boolean;
    error?: boolean;
    topSlot?: React.ReactNode;
    onSearchChange?: (query: string) => void;
    onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
    isLoading?: boolean;
    dropUp?: boolean;
    triggerClassName?: string;
    menuMinWidth?: number | string;
    menuClassName?: string;
    testId?: string;
}

export const CustomSelect = ({
    value,
    onChange,
    options,
    placeholder = 'Select an option',
    className = '',
    triggerClassName = '',
    disabled = false,
    searchable = false,
    isMulti = false,
    showMultiActions = false,
    error = false,
    topSlot,
    onSearchChange,
    onScroll,
    isLoading = false,
    dropUp = false,
    menuMinWidth,
    menuClassName = '',
    testId,
}: CustomSelectProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [hoveredValue, setHoveredValue] = useState<string | null>(null);
    const [shouldDropUp, setShouldDropUp] = useState(false);
    const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const listRef = useRef<HTMLDivElement>(null);
    const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const hasScrolledSelectedOnOpenRef = useRef(false);

    const selectedOptions = useMemo(() => {
        if (isMulti && Array.isArray(value)) {
            return options.filter(opt => value.includes(opt.value));
        }
        return options.filter(opt => opt.value === value);
    }, [options, value, isMulti]);

    const selectedOption = selectedOptions[0];

    const filteredOptions = useMemo(() => {
        if (!searchable || !searchQuery) return options;
        return options.filter(
            (opt) =>
                opt.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                opt.description?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [options, searchable, searchQuery]);

    const updatePosition = useCallback(() => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        setTriggerRect(rect);

        if (!dropUp) {
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            setShouldDropUp(spaceBelow < 300 && spaceAbove > spaceBelow);
        } else {
            setShouldDropUp(true);
        }
    }, [dropUp]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                // Also check if click was on the portal menu
                const menu = document.querySelector('[data-select-menu]');
                if (menu && menu.contains(e.target as Node)) return;
                setIsOpen(false);
            }
        };

        if (isOpen) {
            updatePosition();
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);

            if (searchable && searchInputRef.current) {
                // Focus after a short delay to allow portal to mount
                setTimeout(() => searchInputRef.current?.focus(), 10);
            }
        } else {
            setSearchQuery('');
            setHighlightedIndex(-1);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, searchable, updatePosition]);

    const handleSelect = (optionValue: string) => {
        if (isMulti) {
            const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
            const isSelected = currentValues.includes(optionValue);
            const nextValues = isSelected
                ? currentValues.filter(v => v !== optionValue)
                : [...currentValues, optionValue];
            onChange(nextValues);
        } else {
            onChange(optionValue);
            setIsOpen(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) {
            if (e.key === "Enter" || e.key === "ArrowDown") {
                setIsOpen(true);
            }
            return;
        }

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((prev) =>
                prev < filteredOptions.length - 1 ? prev + 1 : 0
            );
        }

        if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((prev) =>
                prev > 0 ? prev - 1 : filteredOptions.length - 1
            );
        }

        if (e.key === "Enter") {
            e.preventDefault();
            if (highlightedIndex >= 0) {
                const option = filteredOptions[highlightedIndex];
                if (option) {
                    handleSelect(option.value);
                }
            }
        }

        if (e.key === "Escape") {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        if (!isOpen) return;
        const el = optionRefs.current[highlightedIndex];
        if (el) {
            el.scrollIntoView({
                block: "nearest",
            });
        }
    }, [highlightedIndex, isOpen]);

    useEffect(() => {
        if (!isOpen || isMulti || Array.isArray(value) || searchQuery) return;
        if (hasScrolledSelectedOnOpenRef.current) return;

        const selectedIndex = filteredOptions.findIndex((option) => option.value === value);
        if (selectedIndex < 0) return;

        hasScrolledSelectedOnOpenRef.current = true;
        window.setTimeout(() => {
            optionRefs.current[selectedIndex]?.scrollIntoView({
                block: "center",
            });
        }, 0);
    }, [filteredOptions, isMulti, isOpen, searchQuery, value]);

    useEffect(() => {
        if (!isOpen) {
            hasScrolledSelectedOnOpenRef.current = false;
        }
    }, [isOpen]);

    const getMenuStyle = (): React.CSSProperties => {
        if (!triggerRect) return { display: 'none' };

        const parsedMinWidth = menuMinWidth
            ? (typeof menuMinWidth === 'number' ? menuMinWidth : parseInt(String(menuMinWidth), 10))
            : undefined;
        const resolvedWidth = parsedMinWidth
            ? Math.max(triggerRect.width, parsedMinWidth)
            : triggerRect.width;

        return {
            position: 'fixed',
            top: shouldDropUp ? triggerRect.top - 4 : triggerRect.bottom + 4,
            left: triggerRect.left,
            width: resolvedWidth,
            maxWidth: Math.max(resolvedWidth, window.innerWidth - triggerRect.left - 16),
            zIndex: 1000,
            pointerEvents: 'auto',
            transform: shouldDropUp ? 'translateY(-100%)' : 'none',
        };
    };

    return (
        <div ref={containerRef} className={cn("relative w-full outline-none", className)} onKeyDown={handleKeyDown} tabIndex={0}>
            {/* Trigger Button */}
            <button
                type="button"
                data-testid={testId || "select-trigger-button"}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={cn(
                    'w-full min-h-[40px] px-3.5 py-2 border rounded-sm bg-ui-surface-card text-left transition-all duration-150 flex items-center justify-between shadow-xs',
                    disabled
                        ? 'opacity-50 cursor-not-allowed border-ui-hairline bg-ui-surface-soft'
                        : error
                            ? 'border-ui-error focus:ring-1 focus:ring-ui-error/20'
                            : isOpen
                                ? 'border-ui-primary ring-1 ring-ui-primary/20'
                                : 'border-ui-hairline hover:border-ui-hairline-strong hover:bg-ui-surface-soft/40',
                    triggerClassName
                )}
            >
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                    {selectedOption?.icon && (
                        <div className="shrink-0 flex items-center justify-center text-sm min-w-[1.25rem]">
                            {selectedOption.icon}
                        </div>
                    )}
                    <div className="flex-1 min-w-0 overflow-hidden">
                        <span className={cn(
                            "text-sm font-normal truncate block leading-tight",
                            selectedOption ? "text-ui-ink" : "text-ui-muted"
                        )}>
                            {isMulti && Array.isArray(value) && value.length > 0 ? (
                                value.length === 1 ? selectedOptions[0]?.label : `${value.length} selected`
                            ) : (
                                selectedOption?.label || placeholder
                            )}
                        </span>
                    </div>
                </div>
                <ChevronDown
                    className={cn(
                        'w-4 h-4 text-ui-muted shrink-0 transition-transform duration-200',
                        isOpen && 'rotate-180 text-ui-ink'
                    )}
                />
            </button>

            {/* Dropdown Menu Portal */}
            {isOpen && (
                <Portal>
                    <div
                        data-select-menu
                        style={getMenuStyle()}
                        className={cn(
                            'bg-ui-surface-card rounded-sm shadow-xl shadow-slate-900/10 dark:shadow-black/50 border border-ui-hairline overflow-hidden max-w-full animate-in fade-in duration-150',
                            shouldDropUp ? 'slide-in-from-bottom-1' : 'slide-in-from-top-1',
                            menuClassName
                        )}
                    >
                        {searchable && (
                            <div className="p-2 border-b border-ui-hairline-soft">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-ui-muted" />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            onSearchChange?.(e.target.value);
                                        }}
                                        placeholder="Search..."
                                        className="w-full pl-7 pr-4 py-1.5 bg-ui-surface-soft border border-ui-hairline rounded-sm text-xs font-normal text-ui-ink focus:outline-none focus:ring-1 focus:ring-ui-primary/20 placeholder:text-ui-muted"
                                    />
                                </div>
                            </div>
                        )}

                        {topSlot && (
                            <div className="border-b border-ui-hairline-soft p-2">
                                {topSlot}
                            </div>
                        )}

                        <div ref={listRef}
                            onScroll={onScroll}
                            onWheel={(event) => event.stopPropagation()}
                            onTouchMove={(event) => event.stopPropagation()}
                            className="max-h-64 overflow-y-auto overscroll-contain py-1 custom-scrollbar"
                            style={{ WebkitOverflowScrolling: 'touch' }}>
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((option, index) => {
                                    const isHovered =
                                        hoveredValue === option.value ||
                                        filteredOptions[highlightedIndex]?.value === option.value;
                                    const isSelected = isMulti && Array.isArray(value)
                                        ? value.includes(option.value)
                                        : option.value === value;

                                    return (
                                        <button
                                            ref={(el) => {
                                                optionRefs.current[index] = el;
                                            }}
                                            key={option.value}
                                            type="button"
                                            onMouseEnter={() => setHoveredValue(option.value)}
                                            onMouseLeave={() => setHoveredValue(null)}
                                            onClick={() => handleSelect(option.value)}
                                            className={cn(
                                                'group w-full px-3.5 py-2 text-left transition-colors flex items-center gap-2.5 relative text-sm',
                                                isSelected
                                                    ? 'bg-ui-surface-soft text-ui-ink font-medium'
                                                    : isHovered
                                                        ? 'bg-ui-surface-soft/80 text-ui-ink'
                                                        : 'text-ui-body hover:bg-ui-surface-soft/60 hover:text-ui-ink'
                                            )}
                                        >
                                            {option.icon && (
                                                <div className="shrink-0 flex items-center justify-center text-sm min-w-[1.25rem] h-5 w-5">
                                                    {option.icon}
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0 overflow-hidden">
                                                <div className={cn("truncate text-sm", isSelected ? "font-semibold text-ui-ink" : "font-normal")}>
                                                    {option.label}
                                                </div>
                                                {option.description && (
                                                    <div className="text-xs font-normal text-ui-muted truncate mt-0.5">
                                                        {option.description}
                                                    </div>
                                                )}
                                            </div>

                                            {option.actionSlot && (
                                                <div className="shrink-0 flex items-center justify-center ml-2">
                                                    {option.actionSlot}
                                                </div>
                                            )}

                                            {isSelected && (
                                                <Check className="w-4 h-4 text-ui-ink shrink-0 stroke-[2.5]" />
                                            )}
                                        </button>
                                    );
                                })
                            ) : (
                                <div className="px-5 py-8 text-center text-sm text-ui-muted">
                                    {isLoading ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-ui-hairline border-t-ui-primary rounded-full animate-spin" />
                                            <span>Loading...</span>
                                        </div>
                                    ) : (
                                        'No results found'
                                    )}
                                </div>
                            )}
                            {isLoading && filteredOptions.length > 0 && (
                                <div className="px-5 py-3 text-center text-xs text-ui-muted flex items-center justify-center gap-2">
                                    <div className="w-3 h-3 border-2 border-ui-hairline border-t-ui-primary rounded-full animate-spin" />
                                    <span>Loading more...</span>
                                </div>
                            )}
                        </div>
                        {isMulti && showMultiActions && (
                            <div className="flex items-center justify-end gap-2 border-t border-ui-hairline p-2 bg-ui-surface-soft/50">
                                <button
                                    type="button"
                                    onClick={() => onChange([])}
                                    className="rounded-sm px-3 py-1.5 text-xs font-semibold text-ui-body transition-colors hover:bg-ui-surface-soft hover:text-ui-ink"
                                >
                                    Clear
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className="rounded-sm bg-ui-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-ui-primary-active border-transparent shadow-none"
                                >
                                    Done
                                </button>
                            </div>
                        )}
                    </div>
                </Portal>
            )}
        </div>
    );
};
